import { useAuth0 } from '@auth0/auth0-react';
import { useParams } from 'react-router';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import Page from '@/Models/Page';
import http from '@/Utils/HTTP/NewHttpUtils';
import { HideIdentifiedProfilesRequest } from './DTO/HideIdentifiedProfilesRequest';
import { DefaultIdentifiedProfileResponse, IdentifiedProfileResponse } from './DTO/IdentifiedProfileResponse';
import { ShortlistIdentifiedProfilesRequest } from './DTO/ShortlistIdentifiedProfilesRequest';
import useAISearchProgress from './useAISearchProgress';
import useAISearchCounters from './useAISearchCounters';
import SortingDirection from '@/Models/SortingDirection';

export type PossibleMatchScoreRanges = [0, 24] | [25, 49] | [50, 74] | [75, 100];

interface Filters {
    name?: string;
    companyId?: string[];
    matchScoreRange?: PossibleMatchScoreRanges[];
}

type SortBy = { id: keyof Pick<IdentifiedProfileResponse, 'matchScore'> | string; desc: boolean };

export interface IdentifiedProfileOptions {
    filters?: Filters;
    sortBy?: SortBy;
}

const buildQuery = (offset: number, limit: number, opts: IdentifiedProfileOptions): string => {
    const query = new URLSearchParams({
        offset: offset.toString(),
        limit: limit.toString(),
    });

    if (opts.filters?.name) {
        query.append('search', opts.filters.name);
    }

    if (opts.filters?.companyId) {
        opts.filters.companyId.forEach((companyName) => {
            query.append('companyId', companyName);
        });
    }

    if (opts.filters?.matchScoreRange && opts.filters.matchScoreRange.length > 0) {
        const flattenedMatchScoreFilters = opts.filters.matchScoreRange.flat();
        const min = Math.min(...flattenedMatchScoreFilters);
        const max = Math.max(...flattenedMatchScoreFilters);

        query.append('matchScoreMin', min.toString());
        query.append('matchScoreMax', max.toString());
    }

    if (opts.sortBy) {
        query.append('sortByColumn', opts.sortBy.id);
        query.append('sortingDirection', (opts.sortBy.desc ? SortingDirection.Desc : SortingDirection.Asc).toString());
    }

    return query.toString();
};

export interface IdentifiedProfilesCompanyFiltersResponse {
    id: string;
    name: string;
    count: number;
}

export const useIdentifiedProfilesCompanyFilters = () => {
    const { getAccessTokenSilently } = useAuth0();
    const { assessmentId } = useParams();
    const { data, isValidating } = useAISearchProgress();

    return useSWR(
        data?.isInProgress === true || isValidating
            ? null
            : `recruitments/${assessmentId}/ai-search/identified/filters`,
        async (path) => {
            const response = await http.get<IdentifiedProfilesCompanyFiltersResponse[]>({
                path,
                accessToken: await getAccessTokenSilently(),
            });

            return response;
        },
    );
};

export const useIdentifiedProfiles = (limit: number, offset: number, opts: IdentifiedProfileOptions) => {
    const { getAccessTokenSilently } = useAuth0();
    const { assessmentId } = useParams();
    const { data, isValidating } = useAISearchProgress();
    const { mutate: mutateAISearchCounters } = useAISearchCounters();
    const { mutate: mutateIdentifiedProfilesCompanyFilters } = useIdentifiedProfilesCompanyFilters();

    return useSWR(
        // there are two conditions below that blocks the fetch of identified profiles.
        // Condition A
        // if a profile ranking process is ongoing, we block until isInProgress is false.
        // Condition B
        // if no profiles currently ranking, but a recent mutation call is made to revalidate AISearchProgress,
        // we block until backend returns the current state of AISearchProgress for this specific recruitment.
        // Eg. refine search > no matching profiles > AISearchProgress revalidating > AISearchProgress revalidated > isInProgress stays in false
        // This refetch of identified profiles list is necessary to reflect the state of the backend to the frontend when no profiles found without reloading the page.
        data?.isInProgress === true || isValidating
            ? null
            : `recruitments/${assessmentId}/ai-search/identified/?${buildQuery(offset, limit, opts)}`,
        async (path) => {
            const response = await http.get<Page<IdentifiedProfileResponse>>({
                path,
                accessToken: await getAccessTokenSilently(),
            });

            return response || DefaultIdentifiedProfileResponse;
        },
        {
            onSuccess: async () => {
                await mutateAISearchCounters();
                await mutateIdentifiedProfilesCompanyFilters();
            },
        },
    );
};

export const useShortlistIdentifiedProfiles = () => {
    const { getAccessTokenSilently } = useAuth0();
    const { assessmentId } = useParams();

    return useSWRMutation(
        `recruitments/${assessmentId}/ai-search/identified-profile/shortlist`,
        async (path: string, { arg }: { arg: ShortlistIdentifiedProfilesRequest }) => {
            await http.post({
                path,
                accessToken: await getAccessTokenSilently(),
                body: arg,
            });
        },
    );
};

export const useHideIdentifiedProfiles = () => {
    const { getAccessTokenSilently } = useAuth0();
    const { assessmentId } = useParams();

    return useSWRMutation(
        `recruitments/${assessmentId}/ai-search/identified-profile/hide`,
        async (path: string, { arg }: { arg: HideIdentifiedProfilesRequest }) => {
            await http.post({
                path,
                accessToken: await getAccessTokenSilently(),
                body: arg,
            });
        },
    );
};
